import { createComponent } from '@carvana/merch-components';
import { ReactNode, useContext } from 'react';
import { EpicSection, SectionHeader } from '@/vdp/styles';
import Sections from '~/constants/vdp/sections';
import DiscountContext from '~/context/common/DiscountContext';
import StaticContext, {
  useConfiguration,
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useSoonestRoute } from '~/context/vdp/DeliveryPickupContext';
import { useCorrespondingSearchRequestId } from '~/context/vdp/StaticContext';
import { useVehicleAvailability } from '~/context/vdp/UserVehicleContext';
import {
  useGetVehicleDetails,
  useGetVehicleId
} from '~/context/vdp/VehicleContext';
import { useGetShowEVTaxTreatment } from '~/hooks/vdp';
import useShouldShowPriceDetails from '~/hooks/vdp/selectors/useShouldShowPriceDetails';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import { logError } from '~/utilities/loggers';
import PriceDetails from './PriceDetails';
import { CommonMaxWidthDiv } from '../LiteTextBoxes/LiteTextBoxes.styles';

const Container = createComponent(CommonMaxWidthDiv, {
  className: 'mx-auto mt-40 px-24 l:px-0'
});

type OwnProps = {
  children?: ReactNode;
  whiteBg?: boolean;
};

function PriceDetailsWrapper({ children, whiteBg }: OwnProps) {
  const { tradeIncentive } = useContext(DiscountContext);
  const eventCorrelationId = useEventCorrelationId(); // maps browser's events to vehicledetails API's events
  const correspondingSearchRequestId = useCorrespondingSearchRequestId() ?? ''; // maps browser's events to search API's events
  const deliveryPickupRouteId = useSoonestRoute()?.routeId;
  const shouldShowPriceDetails = useShouldShowPriceDetails();
  const { showCkPromo = false } = useConfiguration();
  const { referrer } = useContext(StaticContext);
  const { action } = useVehicleAvailability();
  const {
    fairMarketValue = 0,
    make = '',
    marketValueLoadedDateTime = '',
    model = '',
    price = 0
  } = useGetVehicleDetails();
  const vehicleId = useGetVehicleId();
  const showEVTaxTreatment = useGetShowEVTaxTreatment();

  return shouldShowPriceDetails ? (
    <EpicSection
      className={Sections.General}
      data-nav-section={Sections.PriceDetails}
      whiteBg={whiteBg}
    >
      <SectionHeader>Price Details</SectionHeader>
      <Container>
        <PriceDetails
          analyticsCorrelationId={eventCorrelationId}
          searchRequestId={correspondingSearchRequestId}
          availabilityAction={action}
          deliveryPickupRouteId={deliveryPickupRouteId}
          errorLogger={logError}
          shouldShowCkPromo={showCkPromo}
          shouldShowEvTreatment={showEVTaxTreatment}
          fairMarketValue={fairMarketValue}
          hasTradeIncentive={tradeIncentive > 0}
          make={make}
          marketValueLoadedDateTime={marketValueLoadedDateTime}
          model={model}
          price={price}
          referrer={referrer ?? ''}
          vehicleId={vehicleId}
        />
        {children}
      </Container>
    </EpicSection>
  ) : null;
}

export default withErrorBoundary(
  PriceDetailsWrapper,
  undefined,
  undefined,
  Sections.PriceDetails
);
